/*
 * Bundle cart drawer enhancer.
 *
 * The cart drawer (compiled assets/cart-drawer.min.js) groups bundle lines —
 * lines sharing a `_bundle` property — and renders the `_bundle_main` line as a
 * single card with the other lines listed underneath as chips. Three gaps need
 * fixing in place (the drawer is compiled and can't be edited; the /cart page is
 * handled directly in main-cart.liquid):
 *
 *   1. The card title and image use the main FLAVOR product's. We replace them
 *      with the host (parent) product's, stored on each line as `_bundle_title`
 *      and `_bundle_image`.
 *   2. Each child chip shows the full line-item title ("… - 8 Pack"); we strip
 *      the pack-size variant suffix using Shopify's own `product_title`.
 *   3. The drawer EXCLUDES the `_bundle_main` line from the chips, so the first
 *      flavor is never listed (and a single-flavor bundle lists nothing). We
 *      inject a chip for the main flavor so every flavor + quantity is shown.
 *
 * Card ↔ group matching: the drawer renders exactly one card per `_bundle_main`
 * line, in cart order. We therefore build an ORDERED list of bundle groups (one
 * per main line) and match each card to a group by its product handle, consuming
 * same-handle groups in order. Keying by handle alone is NOT enough — two
 * different bundles can share the same main flavor (e.g. the same pizza added
 * twice), which previously made the groups overwrite each other and cross-
 * contaminate the cards.
 *
 * Scope guard: a bundle card is the only <li> that contains the children
 * container (class `flex flex-wrap gap-2 mt-2`), so standalone product lines are
 * never touched.
 */

(() => {
  const DRAWER_ID = 'cart-drawer';
  const CHILDREN_SELECTOR = '.flex.flex-wrap.gap-2.mt-2';
  const TITLE_SELECTOR = 'a.block.leading-tight';
  const CHIP_CLASS = 'py-0.5 px-1.5 block bg-white border rounded-lg font-display uppercase';

  let groups = []; // ordered, one per _bundle_main line — matches drawer card order
  let fullTitleToProduct = {}; // "Product - Variant" -> "Product" for child chips
  let productTitles = new Set(); // already-stripped titles (idempotency guard)

  let observer = null;

  const handleFromHref = (href) => {
    if (!href) return '';
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  const refreshMap = async () => {
    try {
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // `cache: no-store` + a cache-bust param: right after /cart/add.js the
      // browser may otherwise serve a stale /cart.js, which would leave the
      // freshly-added bundle showing the previous cart's contents until reload.
      const bust = `${rootUrl}cart.js?t=${Date.now()}`;
      const response = await fetch(bust, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();

      const orderedGroups = [];
      const childMap = {};
      const products = new Set();

      (cart.items || []).forEach((item) => {
        const props = item.properties || {};
        if (!props._bundle) return;

        // Map every bundle line's full title to its bare product name.
        if (item.product_title) {
          childMap[item.title] = item.product_title;
          products.add(item.product_title);
        }

        // One entry per bundle group, in cart order. The main line is the card;
        // the drawer omits it from the chips, so we keep its product + quantity
        // to inject it back.
        if (props._bundle_main) {
          orderedGroups.push({
            handle: item.handle || '',
            title: props._bundle_title || '',
            image: props._bundle_image || '',
            mainProduct: item.product_title || '',
            mainQuantity: item.quantity,
          });
        }
      });

      groups = orderedGroups;
      fullTitleToProduct = childMap;
      productTitles = products;
    } catch (error) {
      // Network/parse failure — leave the drawer's default rendering untouched.
    }
  };

  const buildChip = (quantity, product) => {
    const chip = document.createElement('div');
    chip.className = CHIP_CLASS;
    chip.dataset.pbbMain = '1';
    // Remember the rendered value so a later patch can detect a stale chip
    // (e.g. one injected during a mid-add re-render) and replace it.
    chip.dataset.pbbValue = `${quantity} ${product}`;
    const span = document.createElement('span');
    span.append(document.createTextNode(`${quantity} x `));
    const strong = document.createElement('strong');
    strong.textContent = product;
    span.appendChild(strong);
    chip.appendChild(span);
    return chip;
  };

  const patchDrawer = () => {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    // Collect bundle cards in DOM order. The drawer renders one card per
    // _bundle_main line in cart order, so consuming same-handle groups in this
    // order keeps duplicate main flavors matched to the right card.
    const cards = [];
    drawer.querySelectorAll('li').forEach((li) => {
      if (li.querySelector(CHILDREN_SELECTOR)) cards.push(li);
    });

    // Match each card to a group by handle, consuming same-handle groups in
    // order so duplicate main flavors don't collide.
    const cursorByHandle = {};
    const groupForHandle = (handle) => {
      let i = cursorByHandle[handle] || 0;
      for (; i < groups.length; i += 1) {
        if (groups[i].handle === handle) {
          cursorByHandle[handle] = i + 1;
          return groups[i];
        }
      }
      return null;
    };

    cards.forEach((li) => {
      const container = li.querySelector(CHILDREN_SELECTOR);
      const titleEl = li.querySelector(TITLE_SELECTOR);
      const handle = titleEl ? handleFromHref(titleEl.getAttribute('href')) : '';

      // Match by handle, consuming same-handle groups in order. If no group
      // matches (e.g. an orphan line left by the drawer's remove-only-main
      // behaviour, which has no parent metadata), leave the card as the drawer
      // rendered it rather than risk painting another group's data onto it.
      const group = groupForHandle(handle);
      if (!group) return;

      // 1. Card title -> parent product title.
      if (titleEl && group.title && titleEl.textContent.trim() !== group.title) {
        titleEl.textContent = group.title;
      }

      // 1b. Card image -> parent product image. The chips are text-only, so the
      // first <img> in the card is the main line's thumbnail.
      const imgEl = li.querySelector('img');
      if (imgEl && group.image && imgEl.getAttribute('src') !== group.image) {
        imgEl.setAttribute('src', group.image);
        imgEl.removeAttribute('srcset');
      }

      // 2. Strip the pack-size variant suffix from each child chip title.
      container.querySelectorAll('strong').forEach((strong) => {
        const current = strong.textContent.trim();
        if (productTitles.has(current)) return; // already stripped
        const product = fullTitleToProduct[current];
        if (product && product !== current) {
          strong.textContent = product;
        }
      });

      // 3. Inject (or refresh) the main flavor chip — the drawer omits the main
      // line from the chips. A drawer re-render wipes our node, so we re-add it
      // when missing; if a stale chip is present (wrong qty/product from a
      // mid-add patch), we replace it so it never sticks until reload.
      if (group.mainProduct) {
        const existing = container.querySelector('[data-pbb-main]');
        const desired = `${group.mainQuantity} ${group.mainProduct}`;
        if (!existing) {
          container.insertBefore(buildChip(group.mainQuantity, group.mainProduct), container.firstChild);
        } else if (existing.dataset.pbbValue !== desired) {
          existing.replaceWith(buildChip(group.mainQuantity, group.mainProduct));
        }
      }
    });
  };

  // Patch with the observer detached so our own DOM edits don't retrigger it.
  const safePatch = () => {
    if (observer) observer.disconnect();
    patchDrawer();
    const drawer = document.getElementById(DRAWER_ID);
    if (observer && drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }
  };

  const refreshThenPatch = async () => {
    await refreshMap();
    safePatch();
  };

  // Coalesce overlapping refreshes: if a mutation arrives mid-fetch, run exactly
  // one more pass afterwards rather than stacking fetches.
  let refreshing = false;
  let refreshQueued = false;
  const refreshThenPatchOnce = () => {
    if (refreshing) {
      refreshQueued = true;
      return;
    }
    refreshing = true;
    refreshThenPatch().finally(() => {
      refreshing = false;
      if (refreshQueued) {
        refreshQueued = false;
        refreshThenPatchOnce();
      }
    });
  };

  const init = () => {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;

    // The drawer's remove / quantity-change path posts to /cart/update.js and
    // updates its own store WITHOUT firing `update-cart`, so we can't rely on
    // that event to know the cart changed. Instead, treat any structural drawer
    // mutation (add, remove, quantity) as a cue to re-read the cart — otherwise
    // `groups` goes stale and we repaint remaining cards with wrong quantities.
    let frame = 0;
    observer = new MutationObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(refreshThenPatchOnce);
    });
    observer.observe(drawer, { childList: true, subtree: true });

    document.addEventListener('update-cart', refreshThenPatch);
    document.addEventListener('toggle-cart', safePatch);

    refreshThenPatch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
